/**
 * Capital Markets → Fundings: the "ready to fund?" side of the closing
 * readiness signal (closing-readiness.md §5, funding-tape-prep.md).
 *
 *   Forward view   — next N days across both pipelines and both LOS, with
 *                    pull-through and lock-expiration highlights
 *   Funding outlook — expected fundings by date for liquidity prep
 *   Tapes          — tape specs, populated rows with provenance, the
 *                    LOS-vs-document disagreement workflow, the Excel pull
 *
 * Visible to the Capital Markets desk, closers, and super admins (the same
 * gate as the Lock Queue). Writes are gated server-side to the closing team +
 * Capital Markets (`_require_scheduler`).
 */
import { useCallback, useEffect, useState } from "react";

import PaidOutlined from "@mui/icons-material/PaidOutlined";
import { Box } from "@mui/material";

import Flx from "../../components/layout/Flx";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import Txt from "../../components/typography/Txt";
import { fundingsApi } from "../../api/fundingsApi";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useEnsureUsers } from "../../hooks/useEnsureReferenceData";
import { teal } from "../../theme/colors";
import ForwardFundingsView from "./ForwardFundingsView";
import FundingOutlookView from "./FundingOutlookView";
import FundingTapesView from "./FundingTapesView";
import { C } from "./fundingsUi";

const TABS = ["Forward view", "Funding outlook", "Tapes"];

const FundingsScreen = () => {
  useDocumentTitle("Fundings");
  useEnsureUsers();
  const [tab, setTab] = useState(TABS[0]);
  const [tapes, setTapes] = useState([]);
  const [tapesLoading, setTapesLoading] = useState(false);

  // Tape specs are shared by the forward view ("add to tape") and the tapes
  // tab, so they're owned here.
  const loadTapes = useCallback(async () => {
    setTapesLoading(true);
    try {
      const d = await fundingsApi.getTapes();
      setTapes(Array.isArray(d?.tapes) ? d.tapes : []);
    } catch (err) {
      console.error("tapes fetch error:", err);
      setTapes([]);
    } finally {
      setTapesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTapes();
  }, [loadTapes]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        background: C.bg,
        px: { xs: 1.5, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Flx jb wrap g={2} sx={{ alignItems: "flex-end", mb: 2 }}>
        <Box>
          <Flx ac g={1.25} sx={{ mb: 0.5 }}>
            <Flx center sx={{ width: 32, height: 32, borderRadius: "9px", background: teal[500], color: "#fff" }}>
              <PaidOutlined sx={{ fontSize: 18 }} />
            </Flx>
            <Txt sx={{ fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
              Fundings
            </Txt>
          </Flx>
          <Txt sx={{ fontSize: 13, color: C.sub }}>
            Upcoming closings and trade settlements as Capital Markets sees them: what funds, when, and whether it is ready.
          </Txt>
        </Box>
        <ToggleTabSwitcher value={tab} onChange={setTab} tabs={TABS} variant="background" />
      </Flx>

      {tab === TABS[0] && <ForwardFundingsView tapes={tapes} onTapesChanged={loadTapes} />}
      {tab === TABS[1] && <FundingOutlookView />}
      {tab === TABS[2] && (
        <FundingTapesView tapes={tapes} tapesLoading={tapesLoading} onTapesChanged={loadTapes} />
      )}
    </Box>
  );
};

export default FundingsScreen;
